function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import * as React from 'react';
import { Button, SIZE as BUTTON_SIZE, SHAPE as BUTTON_SHAPE } from '../button/index';
import { getOverrides } from '../helpers/overrides';
import { useStyletron } from '../styles/index';
import { ACTION_POSITION, ARTWORK_TYPE, HIERARCHY, KIND } from './constants';
import { StyledRoot, StyledLeadingContent, StyledMessageContent, StyledTitle, StyledMessage, StyledBelowContent, StyledTrailingContent, StyledTrailingButtonContainer, StyledTrailingIconButton } from './styled-components';

function low(theme, kind) {
  switch (kind) {
    case KIND.negative:
      return {
        actionBackgroundColor: theme.colors.bannerActionLowNegative,
        backgroundColor: theme.colors.backgroundLightNegative,
        color: theme.colors.contentPrimary
      };

    case KIND.positive:
      return {
        actionBackgroundColor: theme.colors.bannerActionLowPositive,
        backgroundColor: theme.colors.backgroundLightPositive,
        color: theme.colors.contentPrimary
      };

    case KIND.warning:
      return {
        actionBackgroundColor: theme.colors.bannerActionLowWarning,
        backgroundColor: theme.colors.backgroundLightWarning,
        color: theme.colors.contentPrimary
      };

    case KIND.info:
    default:
      return {
        actionBackgroundColor: theme.colors.bannerActionLowInfo,
        backgroundColor: theme.colors.backgroundLightAccent,
        color: theme.colors.contentPrimary
      };
  }
}

function high(theme, kind) {
  switch (kind) {
    case KIND.negative:
      return {
        actionBackgroundColor: theme.colors.bannerActionHighNegative,
        backgroundColor: theme.colors.backgroundNegative,
        color: theme.colors.contentOnColor
      };

    case KIND.positive:
      return {
        actionBackgroundColor: theme.colors.bannerActionHighPositive,
        backgroundColor: theme.colors.backgroundPositive,
        color: theme.colors.contentOnColor
      };

    case KIND.warning:
      return {
        actionBackgroundColor: theme.colors.bannerActionHighWarning,
        backgroundColor: theme.colors.backgroundWarning,
        color: theme.colors.contentPrimary
      };

    case KIND.info:
    default:
      return {
        actionBackgroundColor: theme.colors.bannerActionHighInfo,
        backgroundColor: theme.colors.backgroundAccent,
        color: theme.colors.contentOnColor
      };
  }
}

function Leading(_ref) {
  var artwork = _ref.artwork;

  var _useStyletron = useStyletron(),
      _useStyletron2 = _slicedToArray(_useStyletron, 2),
      theme = _useStyletron2[1];

  if (!artwork) {
    return null;
  }

  var size = artwork.type === ARTWORK_TYPE.badge ? theme.sizing.scale1000 : theme.sizing.scale800;
  return artwork.icon({
    size: size
  });
}

function Below(_ref2) {
  var action = _ref2.action,
      backgroundColor = _ref2.backgroundColor,
      color = _ref2.color;

  if (!action || action.position !== ACTION_POSITION.below) {
    return null;
  }

  if (process.env.NODE_ENV !== "production") {
    if (action.icon) {
      console.error('Banner ACTION_POSITION.below must not have an icon.');
      return null;
    }
  }

  if (action.label) {
    return /*#__PURE__*/React.createElement(Button, {
      colors: {
        backgroundColor: backgroundColor,
        color: color
      },
      onClick: action.onClick,
      size: BUTTON_SIZE.compact,
      shape: BUTTON_SHAPE.pill
    }, action.label);
  }

  return null;
}

function Trailing(_ref3) {
  var action = _ref3.action,
      backgroundColor = _ref3.backgroundColor,
      color = _ref3.color,
      overrides = _ref3.overrides,
      nested = _ref3.nested;

  var _useStyletron3 = useStyletron(),
      _useStyletron4 = _slicedToArray(_useStyletron3, 2),
      theme = _useStyletron4[1];

  if (!action || action.position && action.position !== ACTION_POSITION.trailing) {
    return null;
  }

  var _getOverrides = getOverrides(overrides.TrailingIconButton, StyledTrailingIconButton),
      _getOverrides2 = _slicedToArray(_getOverrides, 2),
      TrailingIconButton = _getOverrides2[0],
      trailingIconButtonProps = _getOverrides2[1];

  if (action.icon) {
    return /*#__PURE__*/React.createElement(TrailingIconButton, _extends({
      "aria-label": action.label,
      onClick: action.onClick,
      $nested: nested
    }, trailingIconButtonProps), action.icon({
      size: theme.sizing.scale650
    }));
  }

  var _getOverrides3 = getOverrides(overrides.TrailingButtonContainer, StyledTrailingButtonContainer),
      _getOverrides4 = _slicedToArray(_getOverrides3, 2),
      TrailingButtonContainer = _getOverrides4[0],
      trailingButtonContainerProps = _getOverrides4[1];

  if (action.label) {
    return /*#__PURE__*/React.createElement(TrailingButtonContainer, trailingButtonContainerProps, /*#__PURE__*/React.createElement(Button, {
      colors: {
        backgroundColor: backgroundColor,
        color: color
      },
      onClick: action.onClick,
      size: BUTTON_SIZE.compact,
      shape: BUTTON_SHAPE.pill
    }, action.label));
  }

  return null;
}

export function Banner(_ref4) {
  var action = _ref4.action,
      artwork = _ref4.artwork,
      children = _ref4.children,
      _ref4$hierarchy = _ref4.hierarchy,
      hierarchy = _ref4$hierarchy === void 0 ? HIERARCHY.low : _ref4$hierarchy,
      _ref4$kind = _ref4.kind,
      kind = _ref4$kind === void 0 ? KIND.info : _ref4$kind,
      _ref4$overrides = _ref4.overrides,
      overrides = _ref4$overrides === void 0 ? {} : _ref4$overrides,
      _ref4$nested = _ref4.nested,
      nested = _ref4$nested === void 0 ? false : _ref4$nested,
      title = _ref4.title;

  var _useStyletron5 = useStyletron(),
      _useStyletron6 = _slicedToArray(_useStyletron5, 2),
      theme = _useStyletron6[1];

  var styles = hierarchy === HIERARCHY.high ? high(theme, kind) : low(theme, kind);
  var actionPosition = action && action.position ? action.position : ACTION_POSITION.trailing;

  var _getOverrides5 = getOverrides(overrides.Root, StyledRoot),
      _getOverrides6 = _slicedToArray(_getOverrides5, 2),
      Root = _getOverrides6[0],
      rootProps = _getOverrides6[1];

  var _getOverrides7 = getOverrides(overrides.LeadingContent, StyledLeadingContent),
      _getOverrides8 = _slicedToArray(_getOverrides7, 2),
      LeadingContent = _getOverrides8[0],
      leadingContentProps = _getOverrides8[1];

  var _getOverrides9 = getOverrides(overrides.MessageContent, StyledMessageContent),
      _getOverrides10 = _slicedToArray(_getOverrides9, 2),
      MessageContent = _getOverrides10[0],
      messageContentProps = _getOverrides10[1];

  var _getOverrides11 = getOverrides(overrides.TrailingContent, StyledTrailingContent),
      _getOverrides12 = _slicedToArray(_getOverrides11, 2),
      TrailingContent = _getOverrides12[0],
      trailingContentProps = _getOverrides12[1];

  var _getOverrides13 = getOverrides(overrides.BelowContent, StyledBelowContent),
      _getOverrides14 = _slicedToArray(_getOverrides13, 2),
      BelowContent = _getOverrides14[0],
      belowContentProps = _getOverrides14[1];

  var _getOverrides15 = getOverrides(overrides.Title, StyledTitle),
      _getOverrides16 = _slicedToArray(_getOverrides15, 2),
      Title = _getOverrides16[0],
      titleProps = _getOverrides16[1];

  var _getOverrides17 = getOverrides(overrides.Message, StyledMessage),
      _getOverrides18 = _slicedToArray(_getOverrides17, 2),
      Message = _getOverrides18[0],
      messageProps = _getOverrides18[1];

  return /*#__PURE__*/React.createElement(Root, _extends({
    $backgroundColor: styles.backgroundColor,
    $color: styles.color,
    $nested: nested
  }, rootProps), /*#__PURE__*/React.createElement(LeadingContent, _extends({
    $includesArtwork: Boolean(artwork)
  }, leadingContentProps), /*#__PURE__*/React.createElement(Leading, {
    artwork: artwork
  })), /*#__PURE__*/React.createElement(MessageContent, _extends({
    $actionPosition: actionPosition
  }, messageContentProps), Boolean(title) && /*#__PURE__*/React.createElement(Title, titleProps, title), Boolean(children) && /*#__PURE__*/React.createElement(Message, messageProps, children)), /*#__PURE__*/React.createElement(TrailingContent, trailingContentProps, /*#__PURE__*/React.createElement(Trailing, {
    action: action,
    backgroundColor: styles.actionBackgroundColor,
    color: styles.color,
    overrides: overrides,
    nested: nested
  })), /*#__PURE__*/React.createElement(BelowContent, _extends({
    $actionPosition: actionPosition
  }, belowContentProps), /*#__PURE__*/React.createElement(Below, {
    action: action,
    backgroundColor: styles.actionBackgroundColor,
    color: styles.color
  })));
}